import { Button, ButtonIcon } from '@mage-ui/components';
import { cx } from '@mage-ui/styled-system/css';
import {
	button,
	buttonIcon,
	buttonIconIcon,
	containerWrapper,
	focus,
	focusVisible,
	header,
	overflowHidden,
	switchText,
	text,
	textVertical,
} from '@mage-ui/styled-system/recipes';
import { Transition } from '@mantine/core';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { LogoEoiHeader } from '../../data-display/logos/LogoEoiHeader';
import { NavMenu } from '../../navigation/menu/nav-menu/NavMenu';
import { NavMenuLang } from '../../navigation/menu/nav-menu/NavMenuLang';
import { NavWrapper } from '../../navigation/menu/nav-wrapper/NavWrapper';
import { HeaderSocial } from './HeaderSocial';

export const Header = ({
	shopLink,
	menuData,
	menuLangData,
	socialData,
	logoData,
	textAside,
	texts,
	currentLanguage,
	...props
}) => {
	const searchParams = useSearchParams();
	const queryString = searchParams.toString();

	const [isOpen, setIsOpen] = useState(null);

	const toggleMenu = (menu) => {
		setIsOpen((prev) => (prev === menu ? null : menu));
	};

	useEffect(() => {
		const classBody = overflowHidden();
		if (isOpen) {
			document.body.classList.add(classBody);
		} else {
			document.body.classList.remove(classBody);
		}

		return () => {
			document.body.classList.remove(classBody);
		};
	}, [isOpen]);

	return (
		<header
			className={header().root}
			{...(isOpen && { 'data-expanded': true })}
			{...props}
		>
			<div className={containerWrapper()}>
				<div className={header().headerWrapper}>
					<LogoEoiHeader logoData={logoData} />

					<div className={header().toolsWrapper}>
						<Link href={shopLink} className={focus()}>
							<ButtonIcon
								label={texts.cart}
								icon='cart'
								classNames={{
									root: buttonIcon({
										color: 'primary',
									}),
									icon: buttonIconIcon(),
								}}
							/>
						</Link>

						{menuLangData && (
							<ButtonIcon
								onClick={() => toggleMenu('lang')}
								label={texts.language}
								icon='language'
								classNames={{
									root: buttonIcon({
										color: 'primary',
									}),
									icon: buttonIconIcon(),
								}}
								{...(isOpen === 'lang' && { 'aria-expanded': true })}
							/>
						)}
						<ButtonIcon
							onClick={() => toggleMenu('main')}
							label={texts.menu}
							icon='menu'
							classNames={{
								root: cx(buttonIcon({ color: 'primary' }), header().btnMobile),
								icon: buttonIconIcon(),
							}}
							{...(isOpen === 'main' && { 'aria-expanded': true })}
						/>
						<Button
							onClick={() => toggleMenu('main')}
							classNames={{
								root: cx(button(), header().btnDesktop),
							}}
							{...(isOpen === 'main' && { 'aria-expanded': true })}
						>
							<span className={switchText().root}>
								<span
									className={cx(
										switchText().hidden,
										isOpen === 'main' && switchText().visible,
									)}
								>
									{texts.close}
								</span>
								<span
									className={cx(
										switchText().hidden,
										isOpen !== 'main' && switchText().visible,
									)}
								>
									{texts.menu}
								</span>
							</span>
						</Button>
					</div>
				</div>
			</div>
			<Transition
				mounted={isOpen}
				transition='fade'
				duration={200}
				timingFunction='ease'
				keepMounted={true}
			>
				{(transitionStyle) => (
					<>
						<NavWrapper
							style={{ ...transitionStyle, zIndex: 1 }}
							a11yLabel={texts.navigation}
							tabIndex={isOpen ? 0 : -1}
						>
							<div className={containerWrapper()}>
								<NavMenuLang
									queryString={queryString}
									data={menuLangData}
									className={header().menu}
									{...(isOpen === 'lang' && { 'data-opened': true })}
									currentLanguage={currentLanguage}
									a11yLabel={texts.menuLanguage}
								/>
								<NavMenu
									data={menuData}
									className={header().menu}
									{...(isOpen === 'main' && { 'data-opened': true })}
									a11yLabel={texts.menuNav}
								/>
							</div>
						</NavWrapper>
					</>
				)}
			</Transition>
			<div
				className={cx(header().aside, containerWrapper(), 'group')}
				{...(isOpen && { 'data-expanded': true })}
			>
				{socialData && (
					<HeaderSocial
						data={socialData}
						className={header().iconWrapper}
						isOpen={isOpen}
					/>
				)}
				{textAside && (
					<p
						className={cx(
							header().textAside,
							textVertical(),
							text({ size: '4', weight: 'medium' }),
						)}
					>
						{textAside}
					</p>
				)}
			</div>
			{isOpen && (
				<Button
					onClick={() => toggleMenu()}
					aria-controls='nav'
					{...(isOpen && { 'aria-expanded': true })}
					tabIndex={isOpen ? 0 : -1}
					classNames={{
						root: cx(button({ size: 'l' }), header().btnExit, focusVisible()),
					}}
				>
					{texts.exitMenu}
				</Button>
			)}
		</header>
	);
};
